// AssetLoader.js - Final fix with corrected decoder instantiation and naming consistency
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import debug from "debug";

const log = debug("app:glb");

class AssetLoader {
  constructor() {
    this.loader = new GLTFLoader();
    this.dracoLoader = new DRACOLoader();
    this.dracoLoader.setDecoderPath("/draco/");
    this.loader.setDRACOLoader(this.dracoLoader);
    this.meshoptReady = false;

    this._initMeshoptDecoder();
  }

  async _initMeshoptDecoder() {
    try {
      await MeshoptDecoder.ready; // ✅ Correct way to wait for readiness
      this.loader.setMeshoptDecoder(MeshoptDecoder);
      this.meshoptReady = true;
      log("MeshoptDecoder initialized successfully.");
    } catch (error) {
      log("Error initializing MeshoptDecoder:", error);
    }
  }

  cleanup() {
    if (this.dracoLoader) {
      this.dracoLoader.dispose();
      this.dracoLoader = null;
    }

    if (this.loader) {
      this.loader.setDRACOLoader(null);
      this.loader.setMeshoptDecoder(null);
      this.loader = null;
    }

    this.meshoptReady = false;

    log("AssetLoader cleanup complete: DRACO and Meshopt decoders disposed.");
  }

  /**
   * Main asset loading method handling all GLTF/GLB cases
   * @param {Object} param - { key, url }
   * @returns {Object} - { asset, error }
   * @category Experience
   * @subcategory Utils
   */
  async loadAsset({ key, url }) {
    if (!url) throw new Error(`Invalid URL for key: ${key}`);

    try {
      log(`Starting GLTF load: ${url}`);
      const gltf = await this.loader.loadAsync(url);
      const asset = gltf.scene || gltf.scenes[0];

      if (!asset) throw new Error("No valid scene or nodes found in GLTF");

      log("GLTF loaded successfully.");
      return { key, asset, error: null };
    } catch (error) {
      log("Error loading GLTF asset:", error);
      return { key, asset: null, error: error.message };
    }
  }
}

export default AssetLoader;
